import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { User, UserRole, createUser } from "../models/User";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { Org } from "../models/Org";
import {
  deleteUser,
  loadOrgModel,
  loadRecord,
  saveUser,
} from "../api/connection";
import { PageContext } from "../contexts/PageContext";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Check,
  Delete,
  Remove,
  Save,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import Minibar from "../components/Minibar";
import { getRoleColor, getRoleLabel } from "../utils/UserRoles";
import { showConfirmDialog, showSnackbar } from "../components/UniversalDialog";

export default function UserForm() {
  const theme = useTheme();
  const { orgId, userId } = useParams<{
    orgId: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const { isLoggedIn, user, setUser, setOrg } = useContext(PageContext);
  const [organization, setOrganization] = useState<Org | null>(null);
  const [existingUser, setExistingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<User>({
    _id: "",
    userId: "",
    name: "",
    email: "",
    tenantEmail: "",
    role: UserRole.Operator,
    orgId: orgId!,
    tempPassword: "",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [localPart, setLocalPart] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);

  const passwordValidationSchema = Yup.object().shape({
    tempPassword: Yup.string()
      .required("Password is required")
      .test(
        "password-strength",
        "Password must be at least 8 characters long and contain at least three of the following: uppercase letters, lowercase letters, numbers, or special characters.",
        (value) => {
          if (!value) return false;

          const isValidLength = value.length >= 8;
          const hasUppercase = /[A-Z]/.test(value);
          const hasLowercase = /[a-z]/.test(value);
          const hasNumber = /\d/.test(value);
          const hasSpecialChar = /[!@#$%^&*()\-_+={}\[\]|\\:;"'<>,.?/~` ]/.test(
            value
          );

          const validCriteriaCount = [
            hasUppercase,
            hasLowercase,
            hasNumber,
            hasSpecialChar,
          ].filter(Boolean).length;

          return isValidLength && validCriteriaCount >= 3;
        }
      )
      .notOneOf(
        [Yup.ref("username"), null],
        "Password must not be the same as your username or email"
      ),
  });

  useEffect(() => {
    if (orgId) {
      const fetchOrganization =
        async function fetchOrganization(): Promise<void> {
          try {
            const res = await loadRecord<Org>("organizations", orgId);
            if (!res) {
              throw new Error("Organization not found");
            }
            setOrganization(res);
            if (userId && userId !== "new") {
              const findUser = async function findUser(
                userId: string
              ): Promise<void> {
                if (user?.role === UserRole.SystemAdmin) {
                  const foundUser = await loadRecord<User>("users", userId);
                  if (foundUser) {
                    setExistingUser(foundUser);
                  }
                } else if (user?.role === UserRole.OrgAdmin) {
                  const foundUser = createUser(userId);
                  await loadOrgModel(foundUser);
                  setExistingUser(foundUser);
                }
              };
              await findUser(userId);
            }
            setLoading(false);
          } catch (err) {
            console.error("Failed to fetch organization:", err);
          }
        };
      fetchOrganization();
    }
  }, [orgId, userId]);

  const currentUser = existingUser || newUser;
  const domainPart = `${process.env.REACT_APP_TENANT_NAME}.onmicrosoft.${process.env.REACT_APP_TLD}`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLocalPart = event.target.value;
    setLocalPart(newLocalPart);
    setNewUser({ ...newUser, tenantEmail: `${newLocalPart}` });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (existingUser) {
      setExistingUser((prevUser) =>
        prevUser ? { ...prevUser, [name]: value } : null
      );
    } else {
      setNewUser((prevUser) => ({ ...prevUser, [name]: value }));
    }
  };

  const handleDeleteUser = async () => {
    if (!existingUser) {
      return;
    }
    try {
      const resp = await showConfirmDialog("Are you sure?", "Delete User");
      if (!resp) {
        return;
      }
      await deleteUser(existingUser);
      navigate(`/users/${orgId}`);
    } catch (err) {
      showSnackbar("Failed to delete user. Please try again.", "error");
      console.error("Failed to delete user:", err);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const numericValue = value.replace(/\D/g, ""); // Remove non-numeric characters
    if (numericValue.length <= 3) {
      return `(${numericValue}`;
    } else if (numericValue.length <= 6) {
      return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
    } else {
      return `(${numericValue.slice(0, 3)}) ${numericValue.slice(
        3,
        6
      )}-${numericValue.slice(6, 10)}`;
    }
  };

  const isLowercaseChar = (str: string) => /[a-z]/.test(str);
  const isUppercaseChar = (str: string) => /[A-Z]/.test(str);
  const isNumber = (str: string) => /\d/.test(str);
  const isSpecialChar = (str: string) =>
    /[!@#$%^&*()\-_+={}\[\]|\\:;"'<>,.?/~` ]/.test(str);

  const hasMinimumCriteria = () => {
    const validCriteriaCount = [
      isLowercaseChar(formik.values.tempPassword),
      isUppercaseChar(formik.values.tempPassword),
      isNumber(formik.values.tempPassword),
      isSpecialChar(formik.values.tempPassword),
    ].filter(Boolean).length;

    return validCriteriaCount >= 3;
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      tempPassword: "",
      name: currentUser.name,
    },
    validationSchema: passwordValidationSchema,
    onSubmit: (values) => {
      const name = "tempPassword";
      const value = values.tempPassword;
      const name2 = "name";
      const value2 = values.name;
      if (existingUser) {
        setExistingUser((prevUser) =>
          prevUser ? { ...prevUser, [name]: value, [name2]: value2 } : null
        );
      } else {
        setNewUser((prevUser) => ({
          ...prevUser,
          [name]: value,
          [name2]: value2,
        }));
      }
    },
  });

  const handleSaveUser = async () => {
    // Check if form is validating, don't proceed if so
    if (formik.isValidating) {
      return;
    }

    if (!existingUser) {
      newUser.tempPassword = formik.values.tempPassword;
      newUser.name = formik.values.name;
    }

    const u = existingUser || newUser;
    console.log("User to save:", u);
    console.log("name", u.name, "email", u.email, "tenantEmail", u.tenantEmail);
    if (!u.name || !u.email || !u.tenantEmail) {
      showSnackbar("Please fill in all required fields.", "error");
      return;
    }

    if (!formik.isValid) {
      showSnackbar(
        "Invalid Password, make sure requirements are met.",
        "error"
      );
      return;
    }
    setLoading(true);
    if (existingUser) {
      try {
        const res = await saveUser(existingUser);
        if (existingUser._id === user?._id) {
          setUser(res);
          const org = await loadRecord<Org>("organizations", res.orgId);
          if (org) {
            setOrg(org);
          }
        }
        formik.handleSubmit();
        navigate(`/users/${orgId}`);
      } catch (err) {
        console.error("Failed to update user:", err);
      }
    } else {
      try {
        const res = await saveUser(newUser);
        formik.handleSubmit();
        navigate(`/users/${orgId}`);
      } catch (err) {
        showSnackbar(
          "Failed to save user. Make sure your password does not include your username. Please try again.",
          "error"
        );
      }
    }
    setLoading(false);
  };

  const getInitials = (fullName: string) => {
    if (!fullName) {
      return "";
    }
    console.log("fullName", fullName);
    const names = fullName.split(" ");
    const initials = names.map((name) => name[0]?.toUpperCase()).join("");
    return initials.slice(0, 2); // Limit to 2 initials (e.g., "JH")
  };

  if (userId && userId !== "new" && !existingUser && !loading) {
    console.log({ userId, currentUser });

    return (
      <>
        <Stack spacing={2} justifyContent="center" alignItems="center">
          <Typography variant="h1">Page Not Found</Typography>
          <Typography
            color="text.secondary"
            align="center"
            sx={{ width: { xs: "73%", sm: "61%" } }}
          >
            The page you are looking was moved, removed, renamed, or might never
            exist!
          </Typography>
          <Button component={Link} to="/dashboard" variant="contained">
            Back To Dashboard
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* page header */}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            m: 0,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <PageHeader
            title={existingUser ? "Edit User" : "Add User"}
            rightComponent={[
              <Minibar
                minibarItems={[
                  {
                    text: "Save",
                    icon: <Save />,
                    onClick: () => handleSaveUser(),
                  },
                  {
                    text: "Delete",
                    icon: <Delete />,
                    onClick: () => handleDeleteUser(),
                  },
                ]}
                barLength={2}
              />,
            ]}
            // breadcrumbElements={[
            //   {
            //     label: `Dashboard`,
            //     href: "/dashboard",
            //   },
            //   {
            //     label: "Users",
            //     href: `/users/${orgId}`,
            //   },
            //   {
            //     label: currentUser.name || "New User",
            //     href: "",
            //   },
            // ]}
          />

          <Container maxWidth="lg">
            <Grid
              container
              spacing={3}
              justifyContent="center"
              alignItems="center"
            >
              <Grid size={12}>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <Stack spacing={2.5} alignItems="center" sx={{ m: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: getRoleColor(currentUser.role),
                          color: "white",
                          width: 70,
                          height: 70,
                          fontSize: 70 / 2.5,
                        }}
                      >
                        {getInitials(currentUser.name)}
                      </Avatar>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="user-name">Name</InputLabel>
                      <TextField
                        fullWidth
                        // value={currentUser.name || ""}
                        id="user-name"
                        placeholder="Name"
                        autoFocus
                        name="name"
                        // onChange={handleInputChange}
                        value={
                          userId === "new"
                            ? formik.values.name
                            : currentUser.name
                        }
                        // id="user-username"
                        // placeholder="Display Name"
                        // name="username"
                        onChange={
                          userId === "new"
                            ? formik.handleChange
                            : handleInputChange
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="user-role">Role</InputLabel>
                      {userId !== "new" &&
                      user &&
                      (user.role === UserRole.SystemAdmin ||
                        user.role === UserRole.OrgAdmin) &&
                      currentUser.role !== UserRole.SystemAdmin ? (
                        <TextField
                          fullWidth
                          value={currentUser.role}
                          id="user-role"
                          placeholder="ROLE"
                          name="role"
                          onChange={handleInputChange}
                          select
                        >
                          {user.role === UserRole.SystemAdmin && (
                            <MenuItem value={UserRole.OrgAdmin}>
                              Organization Admin
                            </MenuItem>
                          )}
                          <MenuItem value={UserRole.Developer}>
                            Developer
                          </MenuItem>
                          <MenuItem value={UserRole.Operator}>
                            Operator
                          </MenuItem>
                        </TextField>
                      ) : (
                        <TextField
                          fullWidth
                          value={getRoleLabel(currentUser.role)}
                          id="user-role"
                          placeholder="ROLE"
                          name="role"
                          inputProps={{ readOnly: true }}
                        />
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="organization">
                        Organization
                      </InputLabel>
                      <TextField
                        fullWidth
                        value={organization?.name || ""}
                        id="oraganization"
                        placeholder="Organization"
                        name="organization"
                        inputProps={{ readOnly: true }}
                      />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="user-email">Email</InputLabel>
                      <TextField
                        type="email"
                        fullWidth
                        value={currentUser.email || ""}
                        id="user-email"
                        placeholder="Enter Email"
                        name="email"
                        onChange={handleInputChange}
                        inputProps={{ readOnly: userId !== "new" }}
                      />
                    </Stack>
                  </Grid>
                  <Grid size={12}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="user-tenant-email">
                        Tenant Email
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        value={
                          userId === "new" ? localPart : currentUser.tenantEmail
                        }
                        onChange={handleChange}
                        InputProps={
                          userId === "new"
                            ? {
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    style={{ color: "gray" }}
                                  >
                                    @{domainPart}
                                  </InputAdornment>
                                ),
                              }
                            : {}
                        }
                        placeholder="Enter Email"
                        inputProps={{ readOnly: userId !== "new" }}
                        fullWidth
                      />
                    </Stack>
                  </Grid>
                  {userId === "new" && (
                    <>
                      <Grid size={12}>
                        <Stack spacing={1}>
                          <InputLabel htmlFor="user-password">
                            Initial Password
                          </InputLabel>
                          <TextField
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            id="user-password"
                            placeholder="Enter new password"
                            name="tempPassword"
                            onChange={formik.handleChange}
                            value={formik.values.tempPassword}
                            error={
                              formik.touched.tempPassword &&
                              Boolean(formik.errors.tempPassword)
                            }
                            helperText={
                              formik.touched.tempPassword &&
                              formik.errors.tempPassword
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                  >
                                    {showPassword ? (
                                      <Visibility fontSize="small" />
                                    ) : (
                                      <VisibilityOff fontSize="small" />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>
                      </Grid>
                      <Grid size={12}>
                        <Typography variant="h5">
                          Password must contain:
                        </Typography>
                        <List sx={{ p: 0, mt: 1 }}>
                          <ListItem divider sx={{ pl: 4 }}>
                            <ListItemIcon
                              sx={{
                                color:
                                  formik.values.tempPassword.length >= 8
                                    ? "success.main"
                                    : "inherit",
                              }}
                            >
                              {formik.values.tempPassword.length >= 8 ? (
                                <Check />
                              ) : (
                                <Remove />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="At least 8 characters" />
                          </ListItem>
                          <ListItem divider sx={{ pl: 4 }}>
                            <ListItemIcon
                              sx={{
                                color: hasMinimumCriteria()
                                  ? "success.main"
                                  : "inherit",
                              }}
                            >
                              {hasMinimumCriteria() ? <Check /> : <Remove />}
                            </ListItemIcon>
                            <ListItemText primary="Must meet at least 3 out of 4 criteria below:" />
                          </ListItem>
                          <ListItem divider sx={{ pl: 4 }}>
                            <ListItemIcon
                              sx={{
                                color: isLowercaseChar(
                                  formik.values.tempPassword
                                )
                                  ? "success.main"
                                  : "inherit",
                              }}
                            >
                              {isLowercaseChar(formik.values.tempPassword) ? (
                                <Check />
                              ) : (
                                <Remove />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="At least 1 lowercase letter (a-z)" />
                          </ListItem>
                          <ListItem divider sx={{ pl: 4 }}>
                            <ListItemIcon
                              sx={{
                                color: isUppercaseChar(
                                  formik.values.tempPassword
                                )
                                  ? "success.main"
                                  : "inherit",
                              }}
                            >
                              {isUppercaseChar(formik.values.tempPassword) ? (
                                <Check />
                              ) : (
                                <Remove />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="At least 1 uppercase letter (A-Z)" />
                          </ListItem>
                          <ListItem divider sx={{ pl: 4 }}>
                            <ListItemIcon
                              sx={{
                                color: isNumber(formik.values.tempPassword)
                                  ? "success.main"
                                  : "inherit",
                              }}
                            >
                              {isNumber(formik.values.tempPassword) ? (
                                <Check />
                              ) : (
                                <Remove />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="At least 1 number (0-9)" />
                          </ListItem>
                          <ListItem sx={{ pl: 4 }}>
                            <ListItemIcon
                              sx={{
                                color: isSpecialChar(formik.values.tempPassword)
                                  ? "success.main"
                                  : "inherit",
                              }}
                            >
                              {isSpecialChar(formik.values.tempPassword) ? (
                                <Check />
                              ) : (
                                <Remove />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="At least 1 special character" />
                          </ListItem>
                        </List>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
}
